import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';

function CanvasPrintandStretch({ image, onEditImage }) {
  const userImage = image || localStorage.getItem('opticon_last_image');
  const canvasRef = useRef(null);
  /* ===== STATES ===== */
  const [printType, setPrintType] = useState('canvas');
  const [stretchType, setStretchType] = useState('regular');
  const [sideType, setSideType] = useState('mirror');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const DPI = 300;
  const [imageSizeCm, setImageSizeCm] = useState(null);
  /* ===== OPTIONS ===== */
  const printOptions = [
    { id: 'canvas', label: 'Canvas' },
    { id: 'archival', label: 'Archival Mat Finish 310gsm' },
    { id: 'art-decor', label: 'Art Decor Matt 235gsm' }
  ];

  const stretchOptions = [
    { id: 'regular', label: 'Regular 0.75in (19mm)' },
    { id: 'gallery', label: 'Gallery 1.5in (38mm)' }
  ];

  const sideOptions = [
    { id: 'mirror', label: 'Image Mirrored' },
    { id: 'black', label: 'Printed Black' },
    { id: 'white', label: 'Printed White' }
  ];

  /* ===== PRICES ===== */
  const printPriceMap = {
    canvas: 0.1,
    'archival': 0.1,
    'art-decor': 0.1
  };

  const stretchPriceMap = {
    regular: 0.1,
    gallery: 0.1
  };

  const canvasComboPriceMap = {
    regular: 0.1,
    gallery: 0.1
  };

  /* ===== TOTAL PRICE ===== */
  const totalPrice = (() => {
    // Special pricing for Canvas + Stretch
    if (printType === 'canvas') {
      return canvasComboPriceMap[stretchType];
    }

    // Default fallback (other print types)
    return (
      printPriceMap[printType] +
      stretchPriceMap[stretchType]
    );
  })();

  /* ===== GST (10%) ===== */
  const gstAmount = totalPrice * 0.10;
  const totalWithGST = totalPrice + gstAmount;

  /* ===== VISUAL EFFECTS ===== */
  const renderRealCanvas = async () => {
    if (!userImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = userImage;
    await new Promise(resolve => (img.onload = resolve));

    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // 1. DIMENSIONS
    // Depth changes based on selection (Gallery vs Regular)
    const depth = stretchType === 'gallery' ? 55 : 28;
    const skewFactor = 0.35; // The tilt of the side face

    const frontX = depth;
    const frontY = 0;
    const frontW = W - depth - 20; // Leave room for shadow
    const frontH = H - (depth * skewFactor);

    /* =========================================
       SIDE FACE (LEFT ONLY)
    ========================================= */
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(frontX, frontY); // Top right of side (connects to front)
    ctx.lineTo(0, depth * skewFactor); // Top left of side
    ctx.lineTo(0, frontH + (depth * skewFactor)); // Bottom left of side
    ctx.lineTo(frontX, frontH); // Bottom right of side
    ctx.closePath();
    ctx.clip();

    if (sideType === 'mirror') {
      // Create a mirror effect by taking a slice of the image and flipping it
      ctx.save();
      ctx.translate(frontX, 0);
      ctx.scale(-1, 1);
      // Draw a small slice of the left edge of the image, stretched to fill depth
      ctx.drawImage(img, 0, 0, img.width * 0.1, img.height, 0, 0, depth * 2, frontH + (depth * skewFactor));
      ctx.restore();
    } else {
      ctx.fillStyle = sideType === 'black' ? '#111' : '#fff';
      ctx.fill();
    }

    // Add a dark gradient overlay to simulate 3D shading
    const gradient = ctx.createLinearGradient(0, 0, frontX, 0);
    gradient.addColorStop(0, 'rgba(0,0,0,0.3)');
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();

    /* =========================================
       FRONT FACE
    ========================================= */
    ctx.save();
    // Subtle shadow behind the whole block
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 15;
    ctx.shadowOffsetX = 8;
    ctx.shadowOffsetY = 8;

    // Draw the main image starting from where the side face ends
    ctx.drawImage(img, frontX, frontY, frontW, frontH);

    // Subtle inner shadow on the left edge of the front face to show the "fold"
    ctx.beginPath();
    ctx.rect(frontX, frontY, 5, frontH);
    ctx.fillStyle = 'rgba(0,0,0,0.1)';
    ctx.fill();
    ctx.restore();
  };

  useEffect(() => {
    renderRealCanvas();
  }, [stretchType, sideType, userImage]);

  /* ================= EXPORT EDITED IMAGE ================= */
  const exportEditedImage = () => {
    const canvas = canvasRef.current;
    return canvas.toDataURL('image/png'); // REAL edited image
  };

  // Printing filters
  const getPrintFilter = () => {
    switch (printType) {
      case 'canvas': return 'contrast(1.05) saturate(1.1)';
      case 'archival': return 'contrast(1.05) saturate(0.95)';
      case 'art-decor': return 'contrast(0.95) saturate(0.9) brightness(0.98)';
      default: return 'none';
    }
  };

  // Stretching crop simulation
  const getStretchStyle = () => {
    if (stretchType === 'gallery') {
      return { transform: 'scale(1.12)' };   // stronger crop
    }
    return { transform: 'scale(1.03)' };     // light crop
  };

  // Side color simulation
  const getSideOverlay = () => {
    if (sideType === 'black') return 'rgba(0,0,0,0.35)';
    if (sideType === 'white') return 'rgba(255,255,255,0.45)';
    return 'transparent'; // mirror
  };

  /* ================= ADD TO CART ================= */
  const handleAddToCart = async () => {

    if (!exportEditedImage) {
      alert("Image not ready.");
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      /* ================================
         IMAGE → BASE64 (REAL PIPELINE)
      ================================ */

      const imageDataUrl = exportEditedImage(); // returns dataURL/base64

      // convert base64 → blob
      const resImg = await fetch(imageDataUrl);
      const blob = await resImg.blob();

      // blob → base64 (clean)
      const editedImageBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });

      /* ================================
         PAYLOAD
      ================================ */

      const payload = {
        id: 8431,
        quantity: 1,
        meta_data: [
          { key: 'print_type', value: printType },
          { key: 'stretch_type', value: stretchType },
          { key: 'side_type', value: sideType },
          { key: 'total_price', value: totalWithGST },
          { key: 'edited_image', value: editedImageBase64 }
        ]
      };

      console.log('ADD TO CART PAYLOAD:', payload);

      /* ================================
         API CALL
      ================================ */

      const response = await axios.post(
        `https://opticonframing.com.au/wp-json/opticon/v1/canvas-print-and-stretch/add-to-cart`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const percent = Math.round((e.loaded * 100) / e.total);
            setProgress(percent);   // ✅ real dynamic %
          },
        }
      );

      /* ================================
         RESPONSE
      ================================ */

      if (response.data?.success) {
        setProgress(100);

        setTimeout(() => {
          window.location.href = 'https://opticonframing.com.au/cart';
        }, 700);

      } else {
        throw new Error(response.data?.error || 'Server error');
      }

    } catch (err) {
      console.error('❌ Add to cart error:', err);
      alert('Failed to add to cart. Check console for details.');
      setLoading(false);
      setProgress(0);
    }
  };

  useEffect(() => {
    if (!userImage) return;

    const img = new Image();
    img.onload = () => {
      const widthCm = ((img.width / DPI) * 2.54).toFixed(1);
      const heightCm = ((img.height / DPI) * 2.54).toFixed(1);

      setImageSizeCm({
        widthCm,
        heightCm,
        widthPx: img.width,
        heightPx: img.height
      });
    };

    img.src = userImage;
  }, [userImage]);

  return (
    <div className="product-page">

      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          {/* Circular Loader */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF', // Requested Color
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          {/* Percentage Text */}
          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            {progress}%
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* LEFT PREVIEW */}
      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          width={680}
          height={460}
          className="real-canvas-preview"
        />
      </div>



      {/* RIGHT PANEL */}
      <div className="product-info-panel">

        <h1 className="product-title">Canvas Print & Stretch</h1>

        {/* Image Size Info */}
        {imageSizeCm && (
          <div className="cpsimage-size-row">
            <span className="form-label">Image Size</span>
            <span className="cpsimage-size-value">
              {imageSizeCm.widthCm}×{imageSizeCm.heightCm} cm
            </span>

            <button
              type="button"
              className="edit-image-btn"
              onClick={onEditImage}
              title="Edit image"
            >
              <i class="fa-solid fa-pen-to-square"></i>
            </button>
          </div>
        )}

        {/* Printing */}
        <div className="print-option-row">
          <label className="form-label">Printing</label>
          <select className="form-select" value={printType} onChange={e => setPrintType(e.target.value)}>
            {printOptions.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Stretching */}
        <div className="print-option-row">
          <label className="form-label">Stretching</label>
          <select className="form-select" value={stretchType} onChange={e => setStretchType(e.target.value)}>
            {stretchOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label} {stretchPriceMap[opt.id] > 0 ? `(+ $${stretchPriceMap[opt.id]})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Sides */}
        <div className="print-option-row">
          <label className="form-label">Sides</label>
          <select className="form-select" value={sideType} onChange={e => setSideType(e.target.value)}>
            {sideOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Price */}
        <div className="price-cart-row">
          <div className="product-price">${totalPrice.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)} = ${totalWithGST.toFixed(2)}</div>

          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? `${progress}%` : 'Add to Cart'}
          </button>
        </div>

      </div>
    </div>
  );
}

export default CanvasPrintandStretch;